/**
 * JWT Utility Functions
 * Provides secure token decoding with proper validation
 */
/**
 * Safely decode JWT payload without signature verification
 *
 * NOTE: This function decodes the JWT payload for reading claims but does NOT
 * verify the signature. Token signature verification should be performed by the
 * authentication server/library (Okta SDK) before trusting the token.
 *
 * This function is used for:
 * - Reading user information from already-verified tokens
 * - Extracting metadata for display purposes
 * - Creating fallback user objects when getUserInfo API fails
 *
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token) {
    try {
        // Validate token is a non-empty string
        if (!token || typeof token !== 'string') {
            console.warn('Invalid token: not a string or empty');
            return null;
        }
        // Validate JWT structure: must have exactly 3 parts (header.payload.signature)
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.warn('Invalid token structure: expected 3 parts, got', parts.length);
            return null;
        }
        // Get the payload (second part)
        const payloadBase64 = parts[1];
        // Validate payload part is not empty
        if (!payloadBase64) {
            console.warn('Invalid token: empty payload');
            return null;
        }
        // Decode base64 - wrap in try-catch for malformed base64
        let payloadJson;
        try {
            // Replace URL-safe characters (RFC 4648 Section 5)
            let base64 = payloadBase64
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            // Add padding if needed (base64 strings must be multiples of 4)
            while (base64.length % 4 !== 0) {
                base64 += '=';
            }
            payloadJson = atob(base64);
        }
        catch (decodeError) {
            console.warn('Failed to decode base64 payload:', decodeError);
            return null;
        }
        // Parse JSON - wrap in try-catch for malformed JSON
        let payload;
        try {
            payload = JSON.parse(payloadJson);
        }
        catch (parseError) {
            console.warn('Failed to parse JSON payload:', parseError);
            return null;
        }
        // Validate payload is an object
        if (!payload || typeof payload !== 'object') {
            console.warn('Invalid token payload: not an object');
            return null;
        }
        // Optional: Check if token is expired
        if (payload.exp && typeof payload.exp === 'number') {
            const now = Math.floor(Date.now() / 1000);
            if (payload.exp < now) {
                console.warn('Token is expired');
                // Return payload anyway as calling code may want to use it for fallback
            }
        }
        return payload;
    }
    catch (error) {
        console.error('Unexpected error decoding JWT:', error);
        return null;
    }
}
/**
 * Extract user information from JWT token
 *
 * @param token - JWT token string
 * @returns User info object or null if extraction fails
 */
export function extractUserInfoFromToken(token) {
    const payload = decodeJWT(token);
    if (!payload) {
        return null;
    }
    // At minimum, we need a subject
    if (!payload.sub) {
        console.warn('Token payload missing required "sub" claim');
        return null;
    }
    return {
        sub: payload.sub,
        email: payload.email,
        emailVerified: payload.email_verified,
        givenName: payload.given_name,
        familyName: payload.family_name,
        name: payload.name,
        preferredUsername: payload.preferred_username,
        groups: payload.groups,
    };
}
//# sourceMappingURL=jwt.js.map